import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { createContextWithoutDefault } from '../../../../utils/hooks/context'
import Loader from '../../../../components/layout/Loader'
import { FormattedMessage } from 'react-intl'
import { formatChipLabelRessource, mapScheduledTaskApiToApp } from '../utils/ScheduleApiUtils'
import { useRefreshMarker } from '../../../../utils/hooks/refreshMarker'
import { SchedulerViewMode } from '../services/schedule.enums'
import { Ressource, Scheduler, SchedulerParameters, ScheduleViewFiltersValues } from '../services/schedule.types'
import { LocalDate } from '../../../../utils/date/local-date'
import { DateFormat, DateUnit } from '../../../../utils/date/model/date'
import { getCurrentSchedule, getSolutionSchedule } from '../services/schedule.api'
import { ValueListEntry } from '../../../../utils/types/global.types'
import { ELEMENTS_TO_DISPLAY } from '../services/schedule.consts'
import { connect } from 'react-redux'
import { getPreferences } from '../../../common/user/services/userSelectors'
import { readSchedulerFilters, writeSchedulerFilters } from '../services/schedulerFiltersStorage'

export const SchedulerFiltersFormName = 'SchedulerFiltersFormName'

type ScheduleViewFilters = {
	refreshMarker: symbol | undefined
	refreshScheduler: () => void
	handleFilters: (values: Partial<ScheduleViewFiltersValues>) => void
	removeScheduledTask: (id: string) => void
}

const [SchedulerContext, useScheduler] = createContextWithoutDefault<
	SchedulerParameters
	& Scheduler
	& ScheduleViewFilters
	& ScheduleViewFiltersValues
	& { isLoading: boolean }
	& { ressourcesToDisplay: string }
>('ScheduleViewInfos')

type SchedulerProviderProps = {
	ressourcesToDisplay: string
	typeTacheList: ValueListEntry[]
	isResultat: boolean
	idFerme: string
}

const SchedulerProviderComponent = ({ressourcesToDisplay, children, isResultat, typeTacheList, idFerme}: PropsWithChildren<SchedulerProviderProps>) => {
	// Les filtres ne sont mémorisés que pour la vue Ressources (solution + planning actuel), pas pour l'onglet Debug (Outils)
	const persistFilters = ressourcesToDisplay === ELEMENTS_TO_DISPLAY.RESSOURCES

	const [refreshMarker, refresh] = useRefreshMarker('refreshPlanning')
	const [isFirstCall, setIsFirstCall] = useState<boolean>(true)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [scheduler, setScheduler] = useState<Scheduler | null>(null)
	const [viewMode, setViewMode] = useState<SchedulerViewMode>(SchedulerViewMode.TASK_INTERVAL)
	const [error, setError] = useState(false)
	const [filters, setFilters] = useState<ScheduleViewFiltersValues>(() => {
		// nbDays est restauré immédiatement depuis le storage ; les ressources
		// sont résolues après le 1er chargement, une fois les options fraîches disponibles.
		const stored = persistFilters ? readSchedulerFilters(idFerme) : null
		return {
			nbDays: stored?.nbDays ?? 1,
			selectedRessources: [],
			selectedDate: LocalDate.now()
		}
	})

	const handleFilters = useCallback((values: Partial<ScheduleViewFiltersValues>) => {
		// update the values
		setFilters(state => ({
			...state,
			...values
		}))
	}, [])

	const updateScheduler = useCallback(() => {
		setIsLoading(true)

		const promise = isResultat ? getSolutionSchedule : getCurrentSchedule

		const nbDaysParam = filters.nbDays - 1
		const start = filters.selectedDate
		// Filtrage sur 7j = Semaine actuelle
		const formattedStart = filters.nbDays === 7 ? start.startOf(DateUnit.week) : start

		const promiseFilters = {
			period: {
				start: formattedStart.format(DateFormat.SHORT_DATE_WITH_DASH),
				end: formattedStart.plus(nbDaysParam, DateUnit.day).format(DateFormat.SHORT_DATE_WITH_DASH)
			},
			isResultat
		}

		promise(promiseFilters, ressourcesToDisplay)
			.then((r) => {
				let valueRessource = r.viewInfos.ressources
				// utilisé pour l'onglet Debug (cf DebugOnglet.jsx)
				if (ressourcesToDisplay === ELEMENTS_TO_DISPLAY.TOOLS) valueRessource = r.viewInfos.tools

				let rFilterOptions = valueRessource.map((res: Ressource) => ({
					code: res.id,
					chipLabel: formatChipLabelRessource(res.name),
					label: `${res.name} - ${res.role}`,
					active: res.active
				}))

				if (isFirstCall) {
					const stored = persistFilters ? readSchedulerFilters(idFerme) : null
					let selectedRessources
					if (stored) {
						// Filtres déjà mémorisés : on ne conserve que les ressources encore présentes ET actives.
						// Une ressource inactive ou disparue du sélecteur n'est plus affichée (la sélection peut devenir vide).
						// On itère sur les ids mémorisés (et non sur rFilterOptions) afin de conserver l'ordre de sélection de l'utilisateur.
						selectedRessources = stored.selectedRessourceIds
							.map(code => rFilterOptions.find(r => r.active && r.code === code))
							.filter((r): r is (typeof rFilterOptions)[number] => r !== undefined)
					} else {
						// Rien de mémorisé pour cette ferme : défaut = 4 premières ressources actives
						selectedRessources = rFilterOptions.filter(r => r.active).slice(0, Math.min(4, rFilterOptions.length))
					}
					handleFilters({
						...filters,
						selectedRessources
					})
				}

				setScheduler({
					viewInfos: {
						...(r.viewInfos),
						ressourceFilterOptions: rFilterOptions,
						ressources: valueRessource
					},
					tasks: mapScheduledTaskApiToApp(typeTacheList, r.taches)
				})
				setError(false)
			})
			.catch(() => {
				setScheduler(null)
				setError(true)
			})
			.finally(() => {
				setIsLoading(false)
				setIsFirstCall(false)
			})
	}, [setScheduler, filters.selectedDate, filters.nbDays, isResultat])

	useEffect(() => {
		updateScheduler()
	}, [updateScheduler, refreshMarker])

	// Retrait client d'une tâche du calendrier (sans refetch) lors d'une déplanification par drag&drop vers la liste externe
	const removeScheduledTask = useCallback((id: string) => {
		setScheduler(prev => prev ? { ...prev, tasks: prev.tasks.filter(t => `${t.id}` !== `${id}`) } : prev)
	}, [])

	// Mémorise les filtres (nbDays + ids ressources) à chaque changement, une fois l'initialisation résolue.
	// On ignore le 1er rendu (isFirstCall) pour ne pas écraser le storage avec l'état initial vide.
	useEffect(() => {
		if (isFirstCall || !persistFilters) {
			return
		}
		writeSchedulerFilters(idFerme, {
			nbDays: filters.nbDays,
			selectedRessourceIds: filters.selectedRessources.map(r => r.code)
		})
	}, [filters.nbDays, filters.selectedRessources, isFirstCall, persistFilters, idFerme])

	if (error) {
		return (
			<div style={{justifyContent: 'center', alignItems: 'center', flex: 1, display: 'flex'}}>
				<FormattedMessage id="schedule.error.loading" />
			</div>
		)
	}

	if (scheduler == null) {
		return <Loader fromBo={false} />
	}

	return (
		<SchedulerContext.Provider
			value={{
				viewMode,
				changeView: setViewMode,
				...(scheduler || {}),
				isLoading,
				...filters,
				handleFilters,
				removeScheduledTask,
				refreshMarker,
				refreshScheduler: refresh,
				ressourcesToDisplay
			}}
		>
			{children}
		</SchedulerContext.Provider>
	)
}


// idFerme injecté depuis les préférences utilisateur (Redux) — sert de clé de persistance des filtres par ferme
const mapStateToProps = (state: any) => ({
	idFerme: (getPreferences(state).FERME_ID || '0')
})

const SchedulerProvider = connect(mapStateToProps)(SchedulerProviderComponent)

export {
	SchedulerProvider,
	useScheduler
}